import { useState } from "react";
import { ArrowLeft, ExternalLink, Loader2, Check, CircleDot, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type PlaceStatus = "draft" | "public";

interface PlaceEditorTopBarProps {
  placeName: string;
  slug: string;
  dirty: boolean;
  saving: boolean;
  justSaved: boolean;
  isNew?: boolean;
  status: PlaceStatus;
  onStatusChange: (status: PlaceStatus) => void;
  onSave: () => void;
  onBack: () => void;
  confirmOpen: boolean;
  onConfirmLeave: () => void;
  onCancelLeave: () => void;
  requestNavigate: (fn: () => void) => void;
}

/**
 * Sticky action bar for the admin place editor.
 * - Desktop: sticky top bar with all actions visible
 * - Mobile: sticky bottom bar with primary actions + "…" menu for secondary
 */
export function PlaceEditorTopBar({
  placeName,
  slug,
  dirty,
  saving,
  justSaved,
  isNew,
  status,
  onStatusChange,
  onSave,
  onBack,
  confirmOpen,
  onConfirmLeave,
  onCancelLeave,
  requestNavigate,
}: PlaceEditorTopBarProps) {
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<PlaceStatus | null>(null);

  const handleBack = () => requestNavigate(onBack);
  const handleCancel = () => requestNavigate(onBack);

  const handlePreview = () => {
    if (slug) window.open(`/place/${slug}`, "_blank");
  };

  const handleStatusToggle = () => {
    const next: PlaceStatus = status === "public" ? "draft" : "public";
    setPendingStatus(next);
    setPublishDialogOpen(true);
  };

  const confirmStatusChange = () => {
    if (pendingStatus) onStatusChange(pendingStatus);
    setPublishDialogOpen(false);
    setPendingStatus(null);
  };

  const isPublished = status === "public";

  // Dirty/saved indicator (shared)
  const statusIndicator = (
    <>
      {dirty && !saving && (
        <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium">
          <CircleDot className="h-3 w-3" />
          <span className="hidden sm:inline">Ej sparat</span>
        </span>
      )}
      {justSaved && !dirty && (
        <span className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
          <Check className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Sparat</span>
        </span>
      )}
    </>
  );

  // Secondary actions for the "…" menu
  const hasSecondaryActions = !isNew;

  const secondaryMenu = hasSecondaryActions ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Fler åtgärder</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {slug && (
          <DropdownMenuItem onClick={handlePreview}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Förhandsgranska
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleStatusToggle}>
          {isPublished ? "Avpublicera" : "Publicera"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  return (
    <>
      {/* ── Desktop: sticky top bar ── */}
      <div className="hidden md:block sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="flex h-14 items-center justify-between gap-4">
            {/* Left: back */}
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till platser
            </Button>

            {/* Center: title + status badge */}
            <div className="min-w-0 flex-1 flex items-center justify-center gap-2">
              <p className="truncate text-sm font-medium">
                {isNew ? "Ny plats" : `Redigera: ${placeName || "plats"}`}
              </p>
              {!isNew && (
                <Badge
                  variant={isPublished ? "default" : "secondary"}
                  className={isPublished ? "bg-emerald-600 hover:bg-emerald-600 text-white" : ""}
                >
                  {isPublished ? "Publicerad" : "Utkast"}
                </Badge>
              )}
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-2 shrink-0">
              {statusIndicator}

              {/* Cancel */}
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Avbryt
              </Button>

              {/* Save */}
              <Button
                size="sm"
                disabled={(!dirty && !isNew) || saving}
                onClick={onSave}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Sparar…
                  </>
                ) : isNew ? (
                  "Skapa plats"
                ) : (
                  "Spara ändringar"
                )}
              </Button>

              {secondaryMenu}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: sticky bottom action bar ── */}
      <div
        className={cn(
          "md:hidden fixed bottom-[var(--app-bottom-nav-h,0px)] left-0 right-0 z-50",
          "border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80",
          "pb-[env(safe-area-inset-bottom)]"
        )}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-2.5">
          {/* Left: status indicator */}
          <div className="flex items-center gap-2 min-w-0">
            {statusIndicator}
            {!isNew && !dirty && !justSaved && (
              <Badge
                variant={isPublished ? "default" : "secondary"}
                className={cn(
                  "text-[10px] py-0",
                  isPublished ? "bg-emerald-600 hover:bg-emerald-600 text-white" : ""
                )}
              >
                {isPublished ? "Publicerad" : "Utkast"}
              </Badge>
            )}
          </div>

          {/* Right: primary actions + menu */}
          <div className="flex items-center gap-2 shrink-0">
            {secondaryMenu}

            <Button variant="outline" size="sm" onClick={handleCancel}>
              Avbryt
            </Button>

            <Button
              size="sm"
              disabled={(!dirty && !isNew) || saving}
              onClick={onSave}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Sparar…
                </>
              ) : isNew ? (
                "Skapa"
              ) : (
                "Spara"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Mobile: back button (top) ── */}
      <div className="md:hidden px-4 pt-3 pb-1">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Button>
      </div>

      {/* Confirm leave dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={(open) => !open && onCancelLeave()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Osparade ändringar</AlertDialogTitle>
            <AlertDialogDescription>
              Du har osparade ändringar. Vill du lämna utan att spara?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onCancelLeave}>Stanna kvar</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmLeave}>Lämna ändå</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish / Unpublish confirm dialog */}
      <AlertDialog open={publishDialogOpen} onOpenChange={(open) => { if (!open) setPublishDialogOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingStatus === "public" ? "Publicera plats?" : "Avpublicera plats?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStatus === "public"
                ? "Platsen blir synlig för besökare."
                : "Platsen döljs för besökare."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPublishDialogOpen(false)}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>
              {pendingStatus === "public" ? "Publicera" : "Avpublicera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
